
import Button from "../../../../../../../../shared/lib/html5-software-library/src/ranj/components/buttons/Button";
import Slice9Button from "../../../../../../../../shared/lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import ContentTypes from "../../../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import AppModel from "../../../../../../../../shared/src/ranj/app/core/AppModel";
import { AbstractDialogPlayerOptionsMediator } from "../AbstractDialogPlayerOptionsMediator";

/**
* DialogPlayerOptionsFixedButtonsMediator.js
* 
* Created on 8-okt-2013, 15:34:40
*
* @author Ivo Swartjes for Ranj
**/
export default class SingleSelectionOptionsMediator extends AbstractDialogPlayerOptionsMediator {
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, appModel) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, appModel);

        this._speedFactor = AppModel.speedFactor;
        this._optionButtonDict = {};
        /** @type {Button[]} */
        this._buttonArray = [];
    }

    initialize() {
        super.initialize();

        this._optionsSlice9Panel = this.createGenericVerticalSlice9Panel(this._target);
        this._optionsSlice9Panel.update();

        for (let i = 1; i <= 9; i++) {
            const buttonSkin = this._target[`dialogOptionButton${i}`];
            if (!buttonSkin) {
                break;
            }
            buttonSkin.yOrig = buttonSkin.y;
            buttonSkin.scaleOrig = buttonSkin.scaleY;
            const button = new Slice9Button(buttonSkin, Slice9Button.VERTICAL, {
                cacheBitmap: true,
                pixelPerfect: true,
                overlap: false,
                wordWrap: true
            });
            this._decorateButton(button, this._handleOptionButtonClicked.bind(this));
            button.setVisible(false);
            this._buttonArray.push(button);
        }

        this._target.alpha = 0;
    }

    destroy() {
        this._optionButtonDict = {};
        this._buttonArray = [];
        super.destroy();
    }

    // ------------------------------------------
    // 			Dialog displaying
    // ------------------------------------------

    _handleOptionButtonClicked({ currentTarget }) {
        const option = this._optionButtonDict[currentTarget.skin.id];

        let delay = 0;
        const buttons = this._buttonArray.slice(0).reverse();
        buttons.forEach(button => {
            button.setEnabled(false);
            this._animateOptionButtonOut(button.skin, delay);
            delay += 150;
        });
        this._animatePanelOut(this._target, delay);
        delay += 250;

        // Tell delegate that this option was chosen.
        setTimeout(() => {
            const dialogPlayer = this.getDialogPlayer();
            dialogPlayer.optionChosen(option);
            this._close();
        }, delay);
    }

    _clearOptionButtons() {
        for (const button of this._buttonArray) {
            button.setVisible(false);
            delete this._optionButtonDict[button.skin.id];
        }
        this._target.alpha = 0;
    }

    _handleDialogOptionsChanged() {
        this._processDialogOptions();
    }

    _processDialogOptions() {
        // Clear & hide all dialog buttons
        this._clearOptionButtons();

        // Show relevant dialog buttons
        // NB: shallow copy of options
        const currentOptions = this.getDialogPlayer().getCurrentOptions().shuffled();

        // NB: if no options, do nothing - this happens after an option has been selected and the dialog
        // reports that the options have changed (to an empty array).
        if (currentOptions.length <= 0) {
            return;
        }
        if (currentOptions.length > this._buttonArray.length) {
            console.error(`Number of options (${currentOptions.length}) exceeds number of option buttons (${this._buttonArray.length}).`);
        }
        const numOptions = Math.min(currentOptions.length, this._buttonArray.length);

        let yPosition = this._buttonArray[0].skin.y + this._buttonArray[0].getBounds().height;
        for (let i = 0; i < numOptions; i++) {
            const option = currentOptions[i];
            const button = this._buttonArray[i];
            const content = option.getData()?.getData()?.content;
            this._optionButtonDict[button.skin.id] = option;

            if (!content) {
                continue;
            }
            let label;
            switch (content.getType()) {
                case ContentTypes.TYPE_TEXT:
                    label = content.getText();
                    break;
                case ContentTypes.TYPE_KEY_MOMENT_CHOICE:
                    label = content.getChoice();
                    break;
            }
            if (!label) {
                label = "<TEXT NOT FOUND>";
            }
            createjs.Tween.removeTweens(button.skin);
            button.setLabel(label);
            button.setEnabled(true);
            button.setVisible(true);
            // NB: ensure to reset any previous scaling or alpha from animations in order to properly calculate 
            // size for Slice9Panel.
            button.skin.set({
                y: yPosition - button.getBounds().height,
                scaleX: 1,
                scaleY: 1,
                alpha: 1,
            })
            yPosition = button.skin.y - 10;
        }

        let delay = 0;
        this._animatePanelIn(this._target, delay);
        delay += 100;
        
        // NB: animate after updating Slice9Panel
        this._optionsSlice9Panel.update();
        for (const button of this._buttonArray.filter(b => b.getVisible())) {
            this._animateOptionButtonIn(button.skin, delay += 100);
        }
    }

    _animateOptionButtonIn(target, delay) {
        target.visible = true;
        target.alpha = 0;
        target.scaleX = target.scaleY = .1 * target.scaleOrig;
        createjs.Tween.get(target, { override: true })
            .wait(delay / this._speedFactor)
            .to({ alpha: 1, scaleX: target.scaleOrig, scaleY: target.scaleOrig }, 200 / this._speedFactor, createjs.Ease.backOut);
    }

    _animateOptionButtonOut(target, delay) {
        const scale = .1 * target.scaleOrig;
        createjs.Tween.get(target, { override: true })
            .wait(delay / this._speedFactor)
            .to({ alpha: 0, scaleX: scale, scaleY: scale }, 200 / this._speedFactor, createjs.Ease.quadOut)
            .to({ visible: false }, 0);
    }

    _animatePanelIn(target, delay) {
        target.alpha = 0;
        createjs.Tween.get(target, { override: true })
            .wait(delay / this._speedFactor)
            .to({ alpha: 1 }, 200 / this._speedFactor, createjs.Ease.quadOut);
    }

    _animatePanelOut(target, delay) {
        createjs.Tween.get(target, { override: true })
            .wait(delay / this._speedFactor)
            .to({ alpha: 0 }, 200 / this._speedFactor, createjs.Ease.quadOut);
    }
}